import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={style}>
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    plus: 'M12 5v14 M5 12h14',
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2'],
    x: ['M18 6L6 18', 'M6 6l12 12'],
};

export default function VendorContracts({ user }) {
    const [contracts, setContracts] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [vendorFilter, setVendorFilter] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingContract, setEditingContract] = useState(null);
    const [toast, setToast] = useState(null);

    // Form state
    const [vendorId, setVendorId] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [status, setStatus] = useState('Active');

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const fetchContracts = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`/api/vendor-contracts/${user?.operatorId || 'all'}`);
            setContracts(res.data);
        } catch (err) {
            console.error('Fetch contracts error:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchVendors = async () => {
        try {
            const res = await axios.get(`/api/vendors/${user?.operatorId || 'all'}`);
            setVendors(res.data);
        } catch (err) {
            console.error('Fetch vendors error:', err);
        }
    };

    useEffect(() => {
        fetchContracts();
        fetchVendors();
    }, [user]);

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return dateStr.substring(0, 10);
    };

    const filteredContracts = useMemo(() => {
        if (!vendorFilter) return contracts;
        return contracts.filter(c => (c.vendorId?._id || c.vendorId) === vendorFilter);
    }, [contracts, vendorFilter]);

    const openCreateModal = () => {
        setEditingContract(null);
        setVendorId('');
        setStartDate('');
        setEndDate('');
        setStatus('Active');
        setShowModal(true);
    };

    const openEditModal = (contract) => {
        setEditingContract(contract);
        setVendorId(contract.vendorId?._id || contract.vendorId || '');
        setStartDate(contract.startDate ? contract.startDate.substring(0, 10) : '');
        setEndDate(contract.endDate ? contract.endDate.substring(0, 10) : '');
        setStatus(contract.status || 'Active');
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingContract(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                operatorId: user?.operatorId || user?._id,
                vendorId,
                startDate,
                endDate,
                status,
            };

            if (editingContract) {
                await axios.put(`/api/vendor-contracts/${editingContract._id}`, payload);
                showToast('Contract updated successfully');
            } else {
                await axios.post('/api/vendor-contracts', payload);
                showToast('Contract created successfully');
            }

            fetchContracts();
            closeModal();
        } catch (err) {
            console.error('Save contract error:', err);
            showToast(err.response?.data?.message || 'Failed to save contract', 'error');
        }
    };

    const handleDelete = async (contract) => {
        if (!window.confirm('Are you sure you want to delete this contract?')) return;
        try {
            await axios.delete(`/api/vendor-contracts/${contract._id}`);
            showToast('Contract deleted successfully');
            fetchContracts();
        } catch (err) {
            showToast('Failed to delete contract', 'error');
        }
    };

    return (
        <div className="vc-page">
            <style>{`
                .vc-page { padding: 30px; background: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; }

                .vc-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
                .vc-header h1 { font-size: 28px; font-weight: 800; color: #1e293b; margin: 0; }

                .vc-new-btn {
                    background: #6366f1; color: white; border: none; padding: 10px 22px;
                    border-radius: 8px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 8px;
                    transition: all 0.2s; box-shadow: 0 4px 14px rgba(99, 102, 241, 0.3);
                }
                .vc-new-btn:hover { background: #4f46e5; transform: translateY(-1px); box-shadow: 0 6px 18px rgba(99, 102, 241, 0.4); }

                .vendor-filter-bar {
                    display: flex; align-items: center; gap: 14px; margin-bottom: 20px;
                    padding: 14px 20px; background: white; border-radius: 12px;
                    border: 1px solid #e2e8f0; box-shadow: 0 1px 4px rgba(0,0,0,0.03);
                }
                .vendor-filter-bar label {
                    font-size: 13px; font-weight: 600; color: #475569; white-space: nowrap;
                }
                .vendor-filter-select {
                    padding: 9px 14px; border: 1.5px solid #e2e8f0; border-radius: 8px;
                    font-size: 14px; color: #1e293b; background: #f8fafc; outline: none;
                    min-width: 240px; cursor: pointer; transition: all 0.2s;
                    font-family: 'Inter', sans-serif;
                }
                .vendor-filter-select:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1); }

                .vc-card {
                    background: white; border-radius: 16px; border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.03); overflow: hidden;
                }

                .vc-table { width: 100%; border-collapse: collapse; }
                .vc-table th {
                    padding: 14px 24px; text-align: left; font-size: 12px; font-weight: 600;
                    color: #64748b; text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 1px solid #e2e8f0; background: #fff;
                }
                .vc-table td {
                    padding: 16px 24px; border-bottom: 1px solid #f1f5f9; vertical-align: middle;
                    color: #334155;
                }
                .vc-table tbody tr { transition: background 0.15s; }
                .vc-table tbody tr:hover { background: #f8fafc; }
                .vc-table tbody tr:last-child td { border-bottom: none; }

                .vc-vendor-name { font-weight: 600; color: #1e293b; }

                .vc-status-badge {
                    display: inline-block; padding: 4px 14px; border-radius: 99px;
                    font-size: 12px; font-weight: 600;
                }
                .vc-status-active { background: #dcfce7; color: #166534; }
                .vc-status-under-review { background: #fef9c3; color: #92400e; }
                .vc-status-expired { background: #fef3c7; color: #92400e; }
                .vc-status-terminated { background: #fee2e2; color: #991b1b; }

                .vc-actions { display: flex; gap: 8px; }
                .vc-act-btn {
                    border: none; background: none; padding: 7px; cursor: pointer; border-radius: 6px;
                    transition: all 0.2s; display: flex; align-items: center; justify-content: center;
                }
                .vc-act-edit { color: #6366f1; }
                .vc-act-edit:hover { background: #eef2ff; color: #4f46e5; }
                .vc-act-delete { color: #ef4444; }
                .vc-act-delete:hover { background: #fef2f2; color: #dc2626; }

                /* Modal */
                .vc-overlay {
                    position: fixed; inset: 0; background: rgba(15, 23, 42, 0.45);
                    backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 1000;
                    animation: vcFadeIn 0.2s ease;
                }
                @keyframes vcFadeIn { from { opacity: 0; } to { opacity: 1; } }

                .vc-modal {
                    background: white; border-radius: 16px; width: 100%; max-width: 480px;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                    animation: vcSlideUp 0.25s ease;
                }
                @keyframes vcSlideUp { from { transform: translateY(16px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

                .vc-modal-header {
                    padding: 20px 24px; border-bottom: 1px solid #f1f5f9;
                    display: flex; justify-content: space-between; align-items: center;
                }
                .vc-modal-header h2 { font-size: 18px; font-weight: 700; color: #1e293b; margin: 0; }
                .vc-close-btn { background: none; border: none; cursor: pointer; color: #94a3b8; padding: 4px; border-radius: 6px; display: flex; }
                .vc-close-btn:hover { color: #64748b; background: #f1f5f9; }

                .vc-modal-body { padding: 24px; }

                .vc-form-group { display: flex; flex-direction: column; gap: 6px; margin-bottom: 18px; }
                .vc-form-label { font-size: 13px; font-weight: 600; color: #475569; }
                .vc-form-input {
                    padding: 10px 14px; border: 1.5px solid #e2e8f0; border-radius: 10px;
                    outline: none; transition: all 0.2s; background: #fff;
                    font-family: 'Inter', sans-serif; color: #1e293b;
                }
                .vc-form-input:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1); }

                .vc-modal-footer {
                    padding: 18px 24px; border-top: 1px solid #f1f5f9;
                    display: flex; justify-content: flex-end; gap: 12px;
                }
                .vc-cancel-btn {
                    padding: 10px 20px; border: 1.5px solid #e2e8f0; background: white; border-radius: 8px;
                    font-weight: 600; color: #64748b; cursor: pointer; transition: all 0.2s;
                }
                .vc-cancel-btn:hover { background: #f8fafc; border-color: #cbd5e1; }
                .vc-submit-btn {
                    padding: 10px 24px; border: none; background: #6366f1; color: white;
                    border-radius: 8px; font-weight: 600; cursor: pointer;
                    transition: all 0.2s; box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
                }
                .vc-submit-btn:hover { background: #4f46e5; transform: translateY(-1px); }

                .vc-empty { text-align: center; padding: 60px 20px; color: #94a3b8; font-size: 15px; }

                .vc-toast {
                    position: fixed; top: 24px; right: 24px; padding: 14px 24px; border-radius: 10px;
                    color: white; z-index: 2000; font-weight: 500; font-size: 14px;
                    box-shadow: 0 8px 24px rgba(0,0,0,0.15); animation: vcSlideUp 0.3s ease;
                }
                .vc-toast.success { background: #10b981; }
                .vc-toast.error { background: #ef4444; }
            `}</style>

            {toast && <div className={`vc-toast ${toast.type}`}>{toast.msg}</div>}

            <div className="vc-header">
                <h1>Vendor Contracts</h1>
                <button className="vc-new-btn" onClick={openCreateModal}>
                    <Icon d={icons.plus} size={18} /> New Contract
                </button>
            </div>

            <div className="vendor-filter-bar">
                <label>Filter by Vendor:</label>
                <select
                    className="vendor-filter-select"
                    value={vendorFilter}
                    onChange={e => setVendorFilter(e.target.value)}
                >
                    <option value="">All Vendors</option>
                    {vendors.map(v => (
                        <option key={v._id} value={v._id}>{v.vendorName}</option>
                    ))}
                </select>
            </div>

            <div className="vc-card">
                <table className="vc-table">
                    <thead>
                        <tr>
                            <th>Vendor</th>
                            <th>Start Date</th>
                            <th>End Date</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="5" className="vc-empty">Loading contracts...</td></tr>
                        ) : filteredContracts.length === 0 ? (
                            <tr><td colSpan="5" className="vc-empty">No contracts found. Click "New Contract" to add one.</td></tr>
                        ) : (
                            filteredContracts.map(c => (
                                <tr key={c._id}>
                                    <td><span className="vc-vendor-name">{c.vendorId?.vendorName || 'Deleted Vendor'}</span></td>
                                    <td>{formatDate(c.startDate)}</td>
                                    <td>{formatDate(c.endDate)}</td>
                                    <td>
                                        <span className={`vc-status-badge vc-status-${c.status?.toLowerCase().replace(' ', '-')}`}>
                                            {c.status}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="vc-actions">
                                            <button className="vc-act-btn vc-act-edit" title="Edit" onClick={() => openEditModal(c)}>
                                                <Icon d={icons.edit} size={16} />
                                            </button>
                                            <button className="vc-act-btn vc-act-delete" title="Delete" onClick={() => handleDelete(c)}>
                                                <Icon d={icons.trash} size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div className="vc-overlay" onClick={closeModal}>
                    <div className="vc-modal" onClick={e => e.stopPropagation()}>
                        <div className="vc-modal-header">
                            <h2>{editingContract ? 'Edit Contract' : 'New Contract'}</h2>
                            <button className="vc-close-btn" onClick={closeModal}><Icon d={icons.x} size={20} /></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="vc-modal-body">
                                <div className="vc-form-group">
                                    <label className="vc-form-label">Vendor</label>
                                    <select
                                        className="vc-form-input"
                                        required
                                        value={vendorId}
                                        onChange={e => setVendorId(e.target.value)}
                                    >
                                        <option value="">Select a vendor</option>
                                        {vendors.map(v => <option key={v._id} value={v._id}>{v.vendorName}</option>)}
                                    </select>
                                </div>
                                <div className="vc-form-group">
                                    <label className="vc-form-label">Start Date</label>
                                    <input
                                        type="date"
                                        className="vc-form-input"
                                        required
                                        value={startDate}
                                        onChange={e => setStartDate(e.target.value)}
                                    />
                                </div>
                                <div className="vc-form-group">
                                    <label className="vc-form-label">End Date</label>
                                    <input
                                        type="date"
                                        className="vc-form-input"
                                        required
                                        value={endDate}
                                        onChange={e => setEndDate(e.target.value)}
                                    />
                                </div>
                                <div className="vc-form-group">
                                    <label className="vc-form-label">Status</label>
                                    <select
                                        className="vc-form-input"
                                        value={status}
                                        onChange={e => setStatus(e.target.value)}
                                    >
                                        <option value="Under Review">Under Review</option>
                                        <option value="Active">Active</option>
                                        <option value="Expired">Expired</option>
                                        <option value="Terminated">Terminated</option>
                                    </select>
                                </div>
                            </div>
                            <div className="vc-modal-footer">
                                <button type="button" className="vc-cancel-btn" onClick={closeModal}>Cancel</button>
                                <button type="submit" className="vc-submit-btn">
                                    {editingContract ? 'Update Contract' : 'Create Contract'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
